/**
 * `intake-invitation`: an advisor types a prospective client's email address once, and the
 * intake form never asks for it again (crossmarc/steward-app#1555; founder-settled design,
 * 2026-09-18, superseding this issue's original "encode the address in the URL" mechanism —
 * see the issue's correction comment for why that shape was wrong).
 *
 * ## What this replaces
 *
 * Today "Share a snapshot of their finances" (card 1 of the Invite a client dialog) asks the
 * advisor for the client's email, mails the firm's shared intake link to it, and then the
 * verify-first gate asks the CLIENT to type the same address again before anything is
 * readable. Two entries of the same fact, and the second one is the one that counts. This
 * row is the fix: the advisor's typed address lives here, a single-use magic link is mailed,
 * and the rendered form drops the email field entirely on an invited draft because there is
 * nothing left to ask.
 *
 * ## Who writes it, and where
 *
 * Only the façade (`services/steward-app-api`), under the firm caller's own bearer token,
 * into the FIRM's own workspace — never the family's, and appcore is never touched directly
 * (this repo's façade invariant). Create, resend and cancel are all façade routes acting for
 * an authenticated advisor; nothing else in this system writes this row (see the next
 * section for exactly what "nothing else" is pinned by).
 *
 * ## No `opened` or `submitted` state, deliberately — this is the type's central constraint
 *
 ⚠️ **This section used to split a TRUE-TODAY half from a REQUIRED-OF-#1555-slice-3 half that
 * had not been built yet.** That split is now stale and has been corrected below: slice 3
 * landed in the SAME commit that introduced this docblock (`67c1d1c1`), so the two halves were
 * never actually apart in the tree, only in the prose. The correction was made 2026-09-19,
 * verified by reading the tree at `67c1d1c1` (this file's own branch point) — the "REQUIRED,
 * does not exist yet" framing was false from the moment this file merged, not something that
 * went stale afterward. The general caution the old text drew from (state a requirement and a
 * fact in different tenses) still stands; only THIS docblock's application of it was wrong,
 * because the thing it called unbuilt was already built beside it.
 *
 * As of 2026-09-19, verified by reading the source at the same commit:
 *   - The intake-READER cannot reach this type at all. Its session policy is ONE resource,
 *     `GET` on `intake-draft` with a trailing `/*`, every segment of it argued as a control
 *     (`services/intake-reader/src/scopedCredentials.ts`, the "ONE resource, and every segment
 *     of it is a control" block at ~:340). Widening it was considered and REJECTED on #1555:
 *     it costs a property pinned in three layers across two repos, on the one background role
 *     an unauthenticated browser can steer.
 *   - The intake-WRITER's session policy holds SIX `execute-api` resources, the sixth of
 *     which is exactly this type: `GET …/w/<ws>/entities/intake-invitation/*`, READ ONLY, one
 *     instance, no trailing-`/*`-omission (which would open the collection LIST route the
 *     sibling `advisor-intake` GET's own comment names as a hazard for the identical shape)
 *     — see `services/intake-writer/src/scopedCredentials.ts`'s `sessionPolicyFor` docblock
 *     ("Six resources, and each segment of each is a control"), with the matching IAM
 *     statement in `deploy/lib/intake-stack.ts` (the "── The invitation, READ ONLY
 *     (steward-app#1555 slice 3) ──" block: one statement, `GET` only, on the API's
 *     `invitationPath` with a trailing wildcard segment, no write method). Nothing in IAM
 *     stops the writer READING this row, and that same statement's shape is what stops it
 *     writing, listing or deleting one — a bound by shape, not by absence, on the write/
 *     list/delete half only. This is true once this branch has merged and the pack has been
 *     re-seeded (until then the CTYPE itself does not exist, and the writer's GET fails with
 *     the same "not found" error Matt hit), and only in an environment running appcore at or
 *     after appcore#718 — the paragraph below states that second precondition; this one
 *     applies here too, not just there.
 *   - `invitationGenerationRefusal` (`services/intake-writer/src/draft.ts`) already compares a
 *     claim session's carried `linkGeneration` against this row's live value on every save and
 *     submit, and already refuses a session below it. The mechanism the next section describes
 *     is not a plan; it is running.
 *
 * Independently of the above, appcore#718 bounds the writer's `/system/*` scope to
 * `['create', 'read', 'update']`, so `list` and `delete` on this type are refused server-side
 * by appcore whatever steward-app's IAM says. That is a second, independent bound rather than
 * a substitute for the first, and it is live only in an environment running appcore at or
 * after that change — appcore promotes FIRST, which is why.
 *
 * So no process that observes the client actually using the link — opening the form, saving
 * a section, submitting it — is able to write to THIS row. Only the façade, acting for an
 * authenticated advisor, ever does: create, resend, cancel. Whether an invitation was
 * ultimately submitted is answered by looking at the firm's `advisor-intake` rows, not here;
 * this row and the disclosure it invited are deliberately two different rows with no back-
 * pointer from this one to the outcome. **Adding a lifecycle state that reflects client
 * activity (`opened`, `submitted`) means handing SOME background role — the writer or the
 * reader — a write it does not have today, and that is the thing to think twice about before
 * doing it**, not a mechanical schema change.
 *
 * ## `linkGeneration` is what makes resend and cancel actually stop the old link WRITING
 *
 * Every mint of a link copies this row's current `linkGeneration` onto the claim token row it
 * mints, alongside the invitation's own id; both travel from claim token to redeemed draft
 * session. At the client's every save and again at submit, the intake-writer GETs this row and
 * compares its LIVE `linkGeneration` against the value the session carries; a session BELOW the
 * row is refused (`invitationGenerationRefusal`, `services/intake-writer/src/draft.ts` — read it
 * for why a session ABOVE the row is not refused, and for why a session that carries NO
 * generation is refused rather than passed).
 * A **resend** increments the row's value and then mints a new link under the incremented
 * value. A **cancel** increments the row's value and mints nothing further. Either way, any
 * link already sitting in an inbox — possibly forwarded, possibly not yet opened — carries
 * the OLD value, without needing to track or revoke the token itself. Without this field, a
 * resend would produce a working second link while the first kept working too, and a cancel
 * would have no way to stop a link that had already been mailed.
 *
 * ⚠️ **Writes are checked against this row on each message; reads stop once the claim mailer
 * has caught up.** The intake writer reads this row for each save, submit and remove message and
 * refuses one whose session generation is below the row's. The one case it does not refuse is a
 * remove of an id the draft row already records as removed: that may still strip the firm's copy
 * and delete the object, and writes neither the draft row, the session, the address pointer nor a closure stamp (the per-session save-quota count is still made for every draft message; issue #2101). The intake reader cannot read this row (its appcore scope is
 * `intake-draft` alone, crossmarc/appcore#718); it compares against a generation watermark
 * the claim mailer raises when it processes the resend's claim or the cancel's superseded
 * message (crossmarc/steward-app#1558). Until then the superseded link still redeems and
 * still OPENS the form with whatever prefill the draft carries, and if the enqueue failed,
 * until the same request is made again. As of steward-app#1634 (2026-09-21) the Prospects
 * tab's "Invitations sent" section (`AdvisorIntakeInvitations.tsx`) gives the advisor a
 * resend and a cancel control, so a retry after a failed enqueue is a path a user can
 * actually take now, not only an API caller's; a failed cancel enqueue still stays unraised
 * on the reader's side until that retry (or a direct call) happens. Whoever writes the
 * advisor-facing copy for cancel must not promise more than this paragraph.
 *
 * ## This row holds the address in PLAINTEXT, and that is a deliberate departure from the
 * anonymous-claim channel's hashing, not an oversight
 *
 * `packages/intake-claims/src/keys.ts` (`claimEmailHash`, `claimExactEmailHash`) never
 * stores a plaintext verified address, and says so at length. That rule is scoped to ITS
 * channel and was never a blanket rule for every address this system stores: the hashing
 * exists because a claim-table address is caller text from an anonymous stranger who can
 * name any victim, and the row it would otherwise sit in is reachable by a queue-driven role
 * with no authenticated human behind the write. This row's address has an authenticated
 * author (the advisor who typed it) and a firm that owns the record — the same argument that
 * already justifies `sponsoredInvitation.ts` storing its invited address in plaintext (see
 * that file's `email` field and its "as typed, not lowercased" note, which this type follows
 * verbatim below). This file is the second row of that kind, not a new exception to argue
 * afresh.
 *
 * ## Retention is an OPEN question (crossmarc/steward-app#1555), not a TTL to invent here
 *
 * `sponsoredInvitation.ts` keeps its rows forever (see its states list: cancelled and ended
 * are both "kept, never deleted"). This row sits outside the intake retention sweeper's
 * scope in every version of this design, and whether it should age out is undecided pending
 * a founder call recorded on #1555. There is no `ttl` field here and none should be added
 * without that call being made first.
 *
 * ## The states
 *
 *   - (absent) — no working link has ever been sent for this row, AND the façade never even
 *                attempted one. As of crossmarc/steward-app#1647 this is NOT the normal first
 *                moment of a create any more: `createIntakeInvitation` sets `state: 'invited'`
 *                in the SAME write that mints the row's id (`handlers/intakeInvitation.ts`'s
 *                header, "State precedes the send"), so under CURRENT code no write path in
 *                this file ever produces an absent row — every row this handler creates is born
 *                `invited`. ⚠️ A row CAN still read absent in a live environment: one created by
 *                a deploy of the handler from BEFORE #1647 landed, whose soft second write
 *                (the old two-phase order) never happened. That is a pre-existing artifact of
 *                the retired order, not something current code writes; nothing resumes or reuses
 *                it (there is no scan for an earlier `intake-invitation` row — same header,
 *                "State precedes the send" — so a fresh create for the same address mints an
 *                independent second row regardless of whether an old absent one exists). Never
 *                treated as live.
 *   - `invited`   — the façade has ATTEMPTED to enqueue a claim for this row at least once.
 *                   ⚠️ Not "a claim was successfully enqueued": crossmarc/steward-app#1647
 *                   deliberately sets this BEFORE the attempt (so a row a mailed link might
 *                   exist against is never invisible), not after it succeeds, and does not
 *                   revert it if the attempt then fails — see `handlers/intakeInvitation.ts`'s
 *                   header, "State precedes the send", for why a revert was tried and rejected
 *                   on review. So a row can read `invited` with NO working link ever having been
 *                   mailed for it; the accepted cost is that this state is not proof of a send,
 *                   only proof of a genuine attempt, and Resend is always the row's own self-heal
 *                   regardless of which happened. Note what this ALSO does not mean: nothing
 *                   here observes the client OPENING or SUBMITTING, for the IAM reason given
 *                   above.
 *   - `cancelled` — the firm cancelled the invitation. Kept, never deleted (see the retention
 *                   note above; nothing here assumes it eventually will be).
 */
import { z } from 'zod';
import { ULID_PATTERN } from './intakeMessage.js';

export const INTAKE_INVITATION_TYPE_CODE = 'intake-invitation';

export const INTAKE_INVITATION_STATES = ['invited', 'cancelled'] as const;
export type IntakeInvitationState = (typeof INTAKE_INVITATION_STATES)[number];

/**
 * The largest `linkGeneration` either this row or a claim message may carry.
 *
 * 🚨 **A guard against a value that could never be overtaken, not a capacity estimate**
 * (branch security review, 2026-09-18). The writer passes a session whose generation is at or
 * above the row's, so a generation of `Number.MAX_SAFE_INTEGER` anywhere on the path would
 * produce a link that no resend and no cancel could ever supersede. Bounding both ends removes
 * the shape rather than arguing about who could reach it.
 *
 * A real invitation increments once per resend or cancel, by one advisor acting on one client,
 * so a million is several orders of magnitude of headroom: an invitation that reached this cap
 * has a bug or an abuser behind it either way, and refusing the write is the right answer to
 * both. Kept in this file because the ROW is the origin of the value; `intakeDraft.ts`'s
 * message field imports it rather than restating the number.
 */
export const LINK_GENERATION_MAX = 1_000_000;

/**
 * The longest window a stored {@link IntakeInvitationAttributesSchema.linkTtlSeconds} may
 * claim: seven days, in seconds.
 *
 * 🚨 **A bound on a DISPLAY figure, and the unsafe direction is upward**
 * (crossmarc/steward-app#1694). That field exists so the firm portal can show when the link it
 * mailed actually dies; a stored value larger than any window a mailer applies would put a date
 * on the advisor's screen for a link that is already dead, which is the exact defect #1694 was
 * filed about, reintroduced through the field meant to fix it. Nothing reads this value as an
 * authorization input, and no token's life is computed from it — the claim mailer stamps its own
 * expiry from its own constants and never reads this row.
 *
 * ⚠️ **The number is duplicated from `INVITED_CLAIM_TOKEN_TTL_SECONDS`**
 * (`services/steward-app-api/src/intakeClaimConsumer.ts`), because that constant lives in the
 * API service and this package must not depend on it. The duplication is checkable rather than
 * trusted: `services/steward-app-api/src/__tests__/intakeInvitation.test.ts` asserts the two
 * are equal, so raising the mailer's longest window without raising this one reds there rather
 * than silently clamping a stored figure.
 */
export const LINK_TTL_SECONDS_MAX = 7 * 24 * 60 * 60;

const id = z.string().min(1).max(200);
const instant = z.iso.datetime();
/** Identical to `sponsoredInvitation.ts`'s own `emailShape`, deliberately kept as a private
 *  copy rather than an import: this is a STORAGE-shape check, not the input validator, and
 *  each file that has one keeps it beside the schema it guards. The façade's own zod input
 *  schema is what rejects a malformed address before a row is ever written; this only has to
 *  reject what could not possibly be one. */
const emailShape = z.string().min(3).max(320);

export const IntakeInvitationAttributesSchema = z.strictObject({
  firmId: id,
  /** The address the advisor typed, as typed (not lowercased) — the value the claim mailer
   *  addresses and the value the intake-writer stamps onto the submitted intake as
   *  `clientEmail`. Duplicate/throttle comparisons normalize a COPY of this, never the
   *  stored value, so the record keeps what the advisor actually entered. Mirrors
   *  `sponsoredInvitation.ts`'s `email` field exactly, including this property. */
  email: emailShape,
  /** Not written since 2026-10-07: a draft row names its invitation (`invitationId`), and an
   *  invitation no longer names a draft. Optional so a row stored before then still reads.
   *  Server-side only; the façade withholds it from every response. */
  draftId: z.string().regex(ULID_PATTERN).optional(),
  /** Starts at 1. Copied onto the claim token row at every mint, and incremented on this row
   *  by BOTH a resend (before minting the new link) and a cancel (which mints nothing).
   *  See the docblock above: this is the field that makes a resend or a cancel actually stop
   *  an already-mailed link SAVING OR SUBMITTING, rather than merely stop mailing new ones.
   *  It does not stop that link opening the form. Bounded above by {@link LINK_GENERATION_MAX};
   *  see that constant for why an unbounded value is the unsafe direction here. */
  linkGeneration: z.number().int().min(1).max(LINK_GENERATION_MAX),
  createdAt: instant,
  createdBy: id,
  state: z.enum(INTAKE_INVITATION_STATES).optional(),
  lastInvitedAt: instant.optional(),
  /**
   * How long the link mailed by the most recent send is good for, in seconds
   * (crossmarc/steward-app#1681/#1694). Written by the façade on create and rewritten on every
   * resend, from the SAME `liveDraftFor` verdict that the claim message's `existingDraft` flag
   * is built from, so the figure on the row and the figure the mailer applies are two readings
   * of one decision rather than two decisions.
   *
   * 🚨 **A record of what was applied, never an instruction about what to apply.** The claim
   * mailer (`intakeClaimConsumer.ts`) computes the token's expiry from its own constants and
   * the queue message; it never reads this row. Shortening or lengthening this value changes a
   * date on the advisor's screen and nothing about how long any token lives. That is why a
   * merely-implausible value is bounded ({@link LINK_TTL_SECONDS_MAX}) rather than treated as
   * a security input.
   *
   * ⚠️ **Optional because rows written before #1681 do not carry it**, not because a writer
   * may skip it. Every send since writes it. A reader that finds it absent should assume the
   * seven-day window those rows were mailed with — `invitationResource`
   * (`handlers/intakeInvitation.ts`) is the one reader, and does exactly that.
   *
   * ⚠️ It describes the LAST send. A cancelled row keeps whatever its last send applied; the
   * portal shows an expiry only while `state === 'invited'`, so that stale figure is never
   * rendered.
   *
   * ⚠️ The intake-writer parses this same row with this same `strictObject`
   * (`services/intake-writer/src/draft.ts`, `readInvitedAddress`) to read the invited address
   * it records beside a draft. A writer whose build does not know a key on the row reads the
   * row as unreadable, records no address from it and keeps what the draft already stores. It
   * decides "cancelled" before that parse, from `state` and `cancelledAt` alone, so an unknown
   * key does not hide a cancel. The façade's own readers (`storedInvitation`, the list route)
   * use the strict reader.
   */
  linkTtlSeconds: z.number().int().min(1).max(LINK_TTL_SECONDS_MAX).optional(),
  /**
   * The `advisor-intake` this invitation's `draftId` became, when the most recent send REUSED
   * a submitted draft whose form was still open (crossmarc/steward-app#1753 follow-up,
   * 2026-09-28). Written by the façade in the same write as `draftId` on create and on every
   * resend, and dropped by any send that lands on another draft, so the two always describe
   * the same send. Never set for a still-`draft` reuse or a fresh draft.
   *
   * **What it is for:** the Prospects table (`AdvisorIntakes.tsx`'s `invitationDisplayRows`)
   * hides this invitation's own "Invited" row only while THIS intake is a row it renders, so a
   * re-invited prospect shows once. An exact link rather than a match on `clientId` or address,
   * which hid unrelated invitations (independent review of the #1753 branch, H1/M1).
   *
   * ⚠️ Unlike `draftId`, the façade DOES put this on the wire. See
   * `handlers/intakeInvitation.ts`'s header, "One person, one draft", for why naming a
   * submitted intake discloses nothing the firm cannot already see.
   *
   * ⚠️ **Same deploy-skew window as `linkTtlSeconds` above, for the same reason**: the
   * intake-writer parses this row with this `strictObject` and deploys after the façade, so a
   * writer still on the previous build reads a row carrying this key as unreadable (and, on a
   * build before #1697, skips the revocation check too; see `linkTtlSeconds`). Only rows written by a reuse
   * of a submitted draft carry it. Taken under the same "no transition work" ruling.
   */
  reusedIntakeId: z.string().regex(ULID_PATTERN).optional(),
  cancelledAt: instant.optional(),
  cancelledBy: id.optional(),
});

export type IntakeInvitationAttributes = z.infer<typeof IntakeInvitationAttributesSchema>;

/**
 * Reads a stored `intake-invitation` row's attributes. `unreadable` covers both "does not
 * parse" and "is missing" — like `sponsoredInvitation.ts`'s reader and unlike
 * `readIntakeClientWorkspaceLink`, there is no `absent` outer wrapper here because this
 * schema describes the WHOLE row, not one optional key nested inside a larger row; a row of
 * this type that doesn't parse is just broken.
 */
export function readIntakeInvitation(
  value: unknown,
): { kind: 'invitation'; invitation: IntakeInvitationAttributes } | { kind: 'unreadable' } {
  const parsed = IntakeInvitationAttributesSchema.safeParse(value);
  return parsed.success ? { kind: 'invitation', invitation: parsed.data } : { kind: 'unreadable' };
}

/**
 * The fields the intake-writer's REVOCATION decision reads off an invitation row
 * (`invitationGenerationRefusal` and the firm comparison beside it, `services/intake-writer/src/
 * draft.ts`). Exported so a test can pin the list against the strict schema and against
 * {@link readIntakeInvitationRevocation}; adding a field to what the decision reads means adding
 * it here, and the reader's return type only carries these.
 */
export const INTAKE_INVITATION_REVOCATION_FIELDS = ['firmId', 'linkGeneration'] as const;

/** Built from the strict schema's own field definitions (`.shape`), so a bound changed on the
 *  strict side moves here with it. Deliberately NOT strict: an attribute this build does not
 *  know about is not a reason to stop reading the fields it does. */
const IntakeInvitationRevocationSchema = z.object({
  firmId: IntakeInvitationAttributesSchema.shape.firmId,
  linkGeneration: IntakeInvitationAttributesSchema.shape.linkGeneration,
});

/**
 * Reads ONLY the fields the revocation decision needs, tolerating any other attribute
 * (crossmarc/steward-app#1697). {@link readIntakeInvitation} is a `strictObject`, so a row
 * carrying a key this build does not know (a newer façade's write during a deploy window, or any
 * row after a rollback to an older build) is `unreadable` there; this reader answers whether the
 * two fields that decide revocation are present and well-formed regardless.
 *
 * `malformed` means one of those two fields is missing or out of bounds, which this reader does
 * not distinguish from a wrong type. It says nothing about the rest of the row; the caller
 * decides what to do (the intake-writer refuses). It returns only `firmId` and `linkGeneration`, never the rest of the row.
 */
export function readIntakeInvitationRevocation(
  value: unknown,
):
  | { kind: 'fields'; firmId: string; linkGeneration: number }
  | { kind: 'malformed' } {
  const parsed = IntakeInvitationRevocationSchema.safeParse(value);
  return parsed.success
    ? { kind: 'fields', firmId: parsed.data.firmId, linkGeneration: parsed.data.linkGeneration }
    : { kind: 'malformed' };
}

/** `invited` only — the one state a live duplicate-check and a resend/cancel affordance must
 *  treat as "this invitation still has a working link out there". Absent and `cancelled` are
 *  both NOT live: an absent row never had a link mailed, and a cancelled row has had its
 *  `linkGeneration` incremented past what any previously mailed link carries (see that
 *  field's docblock), so a link mailed before cancellation can no longer save or submit, even
 *  though this function alone says nothing about revoking it. */
export function intakeInvitationIsLive(state: IntakeInvitationState | undefined): boolean {
  return state === 'invited';
}
